import { ChangeDetectionStrategy, ChangeDetectorRef, Component, ElementRef, HostListener, Inject, NgZone, OnDestroy, OnInit, PLATFORM_ID, ViewChild } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';

/// The site's own dialogs in place of the browser's alert, confirm and prompt (the owner, 2026-10-24: "fix all browser input
/// and alert to custom"). One host in the app shell draws them, one at a time in the order asked; every page asks through the
/// three functions below and waits for the answer. On the server, or before the host is there, nobody can answer: a
/// confirmation is "no", a prompt "cancelled", a notice passes.
interface DialogAsk {
    kind: 'alert' | 'confirm' | 'prompt';
    message: string;
    value: string;
    resolve: (answer: any) => void;
}

let host: AppDialogHostComponent = null;
const waiting: DialogAsk[] = [];

function ask<T>(kind: DialogAsk['kind'], message: string, value: string, none: T): Promise<T> {
    if (!host) { return Promise.resolve(none); }
    return new Promise<T>(resolve => host.enqueue({ kind, message: String(message == null ? '' : message), value: value == null ? '' : String(value), resolve }));
}

/// "Are you sure?" - true for OK, false for Cancel or Esc.
export function askConfirm(message: string): Promise<boolean> { return ask('confirm', message, '', false); }

/// A line of text from the person, starting at `value` - null when they cancel (as the browser's prompt).
export function askPrompt(message: string, value?: string): Promise<string | null> { return ask<string | null>('prompt', message, value, null); }

/// A notice with one OK.
export function showAlert(message: string): Promise<void> { return ask<void>('alert', message, '', undefined); }

@Component({
    selector: 'app-dialog-host',
    template: `
        <div class="app-dialog" *ngIf="current" role="presentation" (mousedown)="backdrop($event)">
            <div class="app-dialog__box" role="dialog" aria-modal="true" [attr.aria-labelledby]="'app-dialog-msg'" #box>
                <p class="app-dialog__msg" id="app-dialog-msg">{{ current.message }}</p>
                <ng-container *ngIf="current.kind === 'prompt'">
                    <textarea *ngIf="long" class="form-control app-dialog__input" rows="3" [(ngModel)]="value" #field (keydown.enter)="enterInArea($event)"></textarea>
                    <input *ngIf="!long" class="form-control app-dialog__input" type="text" [(ngModel)]="value" #field (keydown.enter)="ok()">
                </ng-container>
                <div class="app-dialog__actions">
                    <button type="button" class="app-dialog__btn" *ngIf="current.kind !== 'alert'" (click)="cancel()">{{ 'cancel' | t:'Cancel' }}</button>
                    <button type="button" class="app-dialog__btn app-dialog__btn--primary" #okButton (click)="ok()">{{ 'dialog-ok' | t:'OK' }}</button>
                </div>
            </div>
        </div>
    `,
    styles: [`
        .app-dialog { position: fixed; inset: 0; z-index: 2000; display: flex; align-items: flex-start; justify-content: center;
            padding: 12vh 16px 16px; background: rgba(16, 24, 40, .45); animation: app-dialog-in .12s ease-out; }
        .app-dialog__box { width: 100%; max-width: 440px; background: #fff; border-radius: 14px; padding: 22px 22px 18px;
            box-shadow: 0 24px 48px -12px rgba(16, 24, 40, .28), 0 0 0 1px rgba(16, 24, 40, .06); }
        .app-dialog__msg { margin: 0 0 14px; font-size: 15px; line-height: 1.5; color: #101828; white-space: pre-line; }
        .app-dialog__input { width: 100%; margin-bottom: 16px; }
        .app-dialog__actions { display: flex; justify-content: flex-end; gap: 8px; }
        .app-dialog__btn { min-width: 84px; height: 38px; padding: 0 16px; border-radius: 10px; border: 1px solid #d0d5dd; background: #fff;
            color: #344054; font-size: 14px; font-weight: 600; cursor: pointer; }
        .app-dialog__btn:hover { background: #f9fafb; }
        .app-dialog__btn--primary { border-color: var(--sl-primary, #1d5bd8); background: var(--sl-primary, #1d5bd8); color: #fff; }
        .app-dialog__btn--primary:hover { filter: brightness(1.06); background: var(--sl-primary, #1d5bd8); }
        .app-dialog__btn:focus-visible { outline: 3px solid rgba(29, 91, 216, .35); outline-offset: 1px; }
        @keyframes app-dialog-in { from { opacity: 0; } to { opacity: 1; } }
    `],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class AppDialogHostComponent implements OnInit, OnDestroy {
    current: DialogAsk = null;
    value = '';
    private lastFocus: HTMLElement = null;

    @ViewChild('field') field: ElementRef<HTMLInputElement | HTMLTextAreaElement>;
    @ViewChild('okButton') okButton: ElementRef<HTMLButtonElement>;

    constructor(private cd: ChangeDetectorRef, private zone: NgZone, @Inject(PLATFORM_ID) private platformId: any) { }

    /// A prompt whose starting text is long, or runs over lines, gets a box of three lines.
    get long(): boolean { return !!this.current && (this.current.value.length > 60 || this.current.value.indexOf('\n') >= 0); }

    ngOnInit(): void {
        if (isPlatformBrowser(this.platformId)) { host = this; }
    }

    ngOnDestroy(): void {
        if (host === this) { host = null; }
    }

    enqueue(item: DialogAsk): void {
        this.zone.run(() => {
            waiting.push(item);
            if (!this.current) { this.next(); }
        });
    }

    ok(): void {
        if (!this.current) { return; }
        const c = this.current;
        this.close(c.kind === 'confirm' ? true : c.kind === 'prompt' ? (this.value == null ? '' : String(this.value)) : undefined);
    }

    cancel(): void {
        if (!this.current) { return; }
        this.close(this.current.kind === 'confirm' ? false : this.current.kind === 'prompt' ? null : undefined);
    }

    /// Enter in a three-line box keeps its new line with Shift; plain Enter answers, as in the one-line box.
    enterInArea(event: KeyboardEvent): void {
        if (!event.shiftKey) { event.preventDefault(); this.ok(); }
    }

    /// A press outside the box is Cancel (a notice: OK) - only when it starts there, so a text selection dragged out is not.
    backdrop(event: MouseEvent): void {
        if (event.target === event.currentTarget) { this.current && this.current.kind === 'alert' ? this.ok() : this.cancel(); }
    }

    @HostListener('document:keydown.escape')
    escape(): void {
        if (this.current) { this.current.kind === 'alert' ? this.ok() : this.cancel(); }
    }

    private close(answer: any): void {
        const c = this.current;
        this.current = null;
        this.cd.markForCheck();
        c.resolve(answer);
        if (this.lastFocus && typeof this.lastFocus.focus === 'function') { try { this.lastFocus.focus(); } catch { } }
        this.lastFocus = null;
        if (waiting.length) { setTimeout(() => this.next()); }
    }

    private next(): void {
        const item = waiting.shift();
        if (!item) { return; }
        this.lastFocus = (typeof document !== 'undefined' ? document.activeElement : null) as HTMLElement;
        this.current = item;
        this.value = item.value;
        this.cd.markForCheck();
        this.cd.detectChanges();
        setTimeout(() => {
            const el = this.field ? this.field.nativeElement : this.okButton ? this.okButton.nativeElement : null;
            if (el) {
                el.focus();
                if (this.field) { (this.field.nativeElement as HTMLInputElement).select(); }
            }
        });
    }
}
